// Journal — the entry feed, ported from design_handoff_oreia/reference/journal-screen.jsx.

import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useEntries } from '../entries/EntriesProvider';
import { EmptyState, Notice, Spinner } from '../components/ui';
import { IconCalendar, IconLock, IconSpark } from '../components/Icons';
import { ConfirmDialog, type ConfirmSpec } from '../components/SettingsKit';
import { groupByDay, publishedEntries } from './home-support';
import { dayKey, JournalCalendar } from './JournalCalendar';
import { JournalGallery } from './JournalGallery';
import { dreamTypeClass, EntryRow } from '../entries/EntryRow';
import { JournalModeSwitch } from '../journals/JournalModeSwitch';
import { longDate } from '../format/dates';
import type { Entry } from '../api/entries';

type Filter = 'all' | 'dream' | 'waking' | 'drafts';
type View = 'list' | 'calendar' | 'gallery';

const FILTERS: Filter[] = ['all', 'dream', 'waking', 'drafts'];

const FILTER_LABEL: Record<Filter, string> = {
  all: 'All',
  dream: 'Dreams',
  waking: 'Waking',
  drafts: 'Drafts',
};

/** The grid icon for the gallery toggle. Local because the shared set has no equivalent shape. */
const IconGrid = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.7} aria-hidden="true">
    <rect x="4" y="4" width="7" height="7" rx="2" />
    <rect x="13" y="4" width="7" height="7" rx="2" />
    <rect x="4" y="13" width="7" height="7" rx="2" />
    <rect x="13" y="13" width="7" height="7" rx="2" />
  </svg>
);

export function JournalScreen() {
  const { entries, isLoading, error, remove } = useEntries();
  const navigate = useNavigate();
  const [confirm, setConfirm] = useState<ConfirmSpec | null>(null);

  /**
   * Deleting from the feed, behind a confirmation.
   *
   * The entry is named in the question, because a row of similar-looking days is exactly where a
   * mis-tap happens, and "Delete this entry?" alone gives no way to tell whether the right one is
   * about to go. No typed word: that ceremony belongs to erasing the account, and asking for it
   * forty times would only teach people to stop reading the dialog.
   */
  const askDelete = (entry: Entry) =>
    setConfirm({
      title: entry.isDraft ? 'Delete this draft?' : 'Delete this entry?',
      body: (
        <>
          <strong>{entry.title || longDate(entry.createdAt)}</strong> and anything Oreia noticed in
          it are removed for good. This cannot be undone.
        </>
      ),
      confirmLabel: entry.isDraft ? 'Delete draft' : 'Delete entry',
      danger: true,
      onConfirm: () => remove(entry.id),
    });

  // Filter, view and day live in the URL so the dashboard can point straight at "my dreams" or "the
  // calendar" — and so a particular day survives a reload or a shared link.
  const [params, setParams] = useSearchParams();
  const filter = (FILTERS.includes((params.get('filter') ?? '') as Filter)
    ? params.get('filter')
    : 'all') as Filter;
  const requested = params.get('view');
  const view: View = requested === 'calendar' || requested === 'gallery' ? requested : 'list';
  const day = params.get('day');

  const setParam = (key: string, value: string | null) => {
    const next = new URLSearchParams(params);
    if (value === null) next.delete(key);
    else next.set(key, value);
    setParams(next, { replace: true });
  };

  /**
   * Switch between the list and one of the richer views.
   *
   * Turning a view off drops the day filter with it: both the calendar and the gallery can narrow to
   * a single day, and the list has nothing on screen that would explain why it came back holding
   * three entries. Switching *between* them keeps the day, so a date chosen in the calendar survives
   * the trip to the grid — as does one that arrived in a shared link.
   */
  const toggleView = (target: Exclude<View, 'list'>) => {
    const next = new URLSearchParams(params);
    if (view === target) {
      next.delete('view');
      next.delete('day');
    } else {
      next.set('view', target);
    }
    setParams(next, { replace: true });
  };

  /**
   * The journal, and the drafts, as two separate stacks.
   *
   * A draft is not a lesser entry in the feed, it is an entry that has not arrived yet — so it is
   * held out of All, Dreams and Waking entirely and given a section of its own, the way an unsent
   * mail sits in Drafts rather than halfway down the inbox.
   */
  const drafts = useMemo(() => entries.filter((e) => e.isDraft), [entries]);
  const published = useMemo(() => publishedEntries(entries), [entries]);

  // The filter's whole set, before the calendar or gallery narrows it to one day — the calendar
  // needs every day that has something on it, not just the day already chosen.
  const pool = useMemo(
    () =>
      filter === 'drafts' ? drafts : filter === 'all' ? published : published.filter((e) => e.kind === filter),
    [drafts, published, filter],
  );

  const visible = useMemo(
    () => (day ? pool.filter((e) => dayKey(new Date(e.createdAt)) === day) : pool),
    [pool, day],
  );
  const groups = useMemo(() => groupByDay(visible), [visible]);

  const dreams = useMemo(() => published.filter((e) => e.kind === 'dream'), [published]);
  const waking = published.length - dreams.length;

  const dreamCounts = useMemo(() => {
    const counts = { normal: 0, lucid: 0, night: 0 };
    for (const dream of dreams) {
      counts[dreamTypeClass(dream.dreamType ?? 'normal') as keyof typeof counts]++;
    }
    return counts;
  }, [dreams]);

  return (
    <>
      <div className="ohead">
        <div className="obrand">
          <span className="nm" style={{ fontSize: 22 }}>Journal</span>
        </div>
        <div className="olock">
          <IconLock /> Private
        </div>
      </div>

      <JournalModeSwitch mode="entries" />

      <div className="ojhead">
        <h1>
          Your entries,
          <br />
          <em className="oitalic">in order.</em>
        </h1>
        <p className="ojsub">
          {published.length === 0
            ? 'Nothing written yet.'
            : `${published.length} ${published.length === 1 ? 'entry' : 'entries'} · ${dreams.length} ${dreams.length === 1 ? 'dream' : 'dreams'}`}
        </p>
      </div>

      {filter === 'dream' && dreams.length > 0 ? (
        <div className="odtypes">
          <div className="odtype normal">
            <div className="n">{dreamCounts.normal}</div>
            <div className="l">
              <span className="d" />
              Dreams
            </div>
          </div>
          <div className="odtype lucid">
            <div className="n">{dreamCounts.lucid}</div>
            <div className="l">
              <span className="d" />
              Lucid
            </div>
          </div>
          <div className="odtype night">
            <div className="n">{dreamCounts.night}</div>
            <div className="l">
              <span className="d" />
              Nightmares
            </div>
          </div>
        </div>
      ) : null}

      {filter === 'waking' && waking > 0 ? (
        <div className="odtypes single">
          <div className="odtype">
            <div className="n">{waking}</div>
            <div className="l">Waking entries</div>
          </div>
        </div>
      ) : null}

      <div className="ofilters" role="tablist" aria-label="Filter entries">
        {FILTERS.map((value) =>
          // Drafts only exists when there are drafts. An always-present tab that is empty for almost
          // everyone is a permanent reminder of nothing.
          value === 'drafts' && drafts.length === 0 ? null : (
            <button
              key={value}
              role="tab"
              aria-selected={filter === value}
              className={`ochip${filter === value ? ' active' : ''}`}
              onClick={() => setParam('filter', value === 'all' ? null : value)}
            >
              {FILTER_LABEL[value]}
              {value === 'drafts' ? ` ${drafts.length}` : ''}
            </button>
          ),
        )}
        {/* The two view toggles are one control in two halves, so they are wrapped rather than left
            loose among the filters: with Drafts in the row there is no longer space for six chips on
            a phone, and a wrapping flex would drop one of the pair to the next line on its own. */}
        <div className="oview-toggles">
          <button
            className={`ochip ogal-toggle${view === 'gallery' ? ' active' : ''}`}
            onClick={() => toggleView('gallery')}
            aria-pressed={view === 'gallery'}
          >
            {/* Spans, not bare text: below the shell's 640px breakpoint the labels are hidden and
                the pair shrinks to icons. The label stays in the accessibility tree either way. */}
            <IconGrid /> <span className="ochip-label">Gallery</span>
          </button>
          <button
            className={`ochip ocal-toggle${view === 'calendar' ? ' active' : ''}`}
            onClick={() => toggleView('calendar')}
            aria-pressed={view === 'calendar'}
          >
            <IconCalendar /> <span className="ochip-label">Calendar</span>
          </button>
        </div>
      </div>

      {view === 'calendar' ? (
        <JournalCalendar
          entries={pool}
          selected={day}
          onSelect={(next) => setParam('day', next)}
        />
      ) : null}

      {error ? <Notice tone="danger">{error}</Notice> : null}

      {isLoading ? (
        <Spinner label="Decrypting your entries…" />
      ) : visible.length === 0 ? (
        <EmptyState
          title={
            filter === 'drafts'
              ? 'Nothing unfinished.'
              : entries.length === 0
                ? 'Nothing here yet.'
                : 'Nothing in this filter.'
          }
          body={
            filter === 'drafts'
              ? 'Anything you start and leave is kept here until you finish it.'
              : entries.length === 0
                ? 'Write one line about today, or last night, and Oreia starts from there.'
                : 'Try a different filter, or write something new.'
          }
          action={
            <button className="octa" onClick={() => navigate('/write')}>
              <IconSpark /> New entry
            </button>
          }
        />
      ) : view === 'gallery' ? (
        <JournalGallery
          entries={visible}
          day={day}
          onClearDay={() => setParam('day', null)}
          onOpen={(id) => navigate(`/write/${id}`)}
        />
      ) : (
        groups.map((group) => (
          <div key={group.day}>
            <div className="oday">{group.day}</div>
            {group.items.map((entry) => (
              <EntryRow
                key={entry.id}
                entry={entry}
                onOpen={() => navigate(`/write/${entry.id}`)}
                onDelete={() => askDelete(entry)}
              />
            ))}
          </div>
        ))
      )}

      <ConfirmDialog spec={confirm} onClose={() => setConfirm(null)} />

      <div style={{ height: 60 }} />
    </>
  );
}
